import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import {
  collection,
  addDoc,
  onSnapshot,
  query,
  orderBy,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp
} from 'firebase/firestore';

// Custom hook for mentor management
export const useMentorManagement = () => {
  const [mentors, setMentors] = useState([]);
  const [newMentor, setNewMentor] = useState({
    name: '',
    bio: '',
    subjects: '',
    hoursAvailable: '',
    website: ''
  });

  useEffect(() => {
    // Set up real-time listener for mentors
    const q = query(collection(db, 'mentors'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const mentorList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));
      setMentors(mentorList);
    });

    return () => unsubscribe();
  }, []);

  return {
    mentors,
    newMentor,
    setNewMentor
  };
};

// Custom hook for mentor connection requests
export const useMentorRequests = () => {
  const [mentorRequests, setMentorRequests] = useState([]);

  useEffect(() => {
    // Set up real-time listener for mentor requests
    const q = query(collection(db, 'mentorRequests'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const requestList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));
      setMentorRequests(requestList);
    });

    return () => unsubscribe();
  }, []);

  return {
    mentorRequests
  };
};

// Mentor Creation Form Component (Teacher only)
export const MentorCreationForm = ({ newMentor, setNewMentor, onSubmit }) => {
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newMentor.name.trim() || !newMentor.bio.trim() || !newMentor.subjects.trim()) {
      alert('Please fill in at least the name, bio, and subjects fields.');
      return;
    }

    // Normalize website URL - add https:// if no protocol is specified, and www. if missing
    let normalizedWebsite = '';
    if (newMentor.website.trim()) {
      normalizedWebsite = newMentor.website.trim();
      // Add protocol if missing
      if (!normalizedWebsite.match(/^https?:\/\//i)) {
        normalizedWebsite = 'https://' + normalizedWebsite;
      }
      // Add www. if missing (after the protocol)
      if (!normalizedWebsite.match(/^https?:\/\/www\./i)) {
        normalizedWebsite = normalizedWebsite.replace(/^(https?:\/\/)/, '$1www.');
      }
    }

    try {
      await addDoc(collection(db, 'mentors'), {
        name: newMentor.name.trim(),
        bio: newMentor.bio.trim(),
        subjects: newMentor.subjects.trim(),
        hoursAvailable: newMentor.hoursAvailable.trim() || '0',
        website: normalizedWebsite,
        createdAt: new Date()
      });

      // Reset form
      setNewMentor({
        name: '',
        bio: '',
        subjects: '',
        hoursAvailable: '',
        website: ''
      });

      if (onSubmit) onSubmit();
    } catch (error) {
      console.error('Error adding mentor:', error);
      alert('Failed to add mentor. Please try again.');
    }
  };

  return (
    <div className="create-post">
      <h2>Add New Mentor</h2>
      <form onSubmit={handleSubmit}>
        <input
          type="text"
          className="post-title-input"
          placeholder="Mentor's Full Name *"
          value={newMentor.name}
          onChange={(e) => setNewMentor({ ...newMentor, name: e.target.value })}
          required
        />

        <textarea
          placeholder="Enter mentor's bio and background *"
          value={newMentor.bio}
          onChange={(e) => setNewMentor({ ...newMentor, bio: e.target.value })}
          rows="4"
          required
        />

        <input
          type="text"
          className="post-title-input"
          placeholder="Subjects of Interest (e.g., Mathematics, Computer Science) *"
          value={newMentor.subjects}
          onChange={(e) => setNewMentor({ ...newMentor, subjects: e.target.value })}
          required
        />

        <input
          type="number"
          className="post-title-input"
          placeholder="Hours Available per Year (optional)"
          value={newMentor.hoursAvailable}
          onChange={(e) => setNewMentor({ ...newMentor, hoursAvailable: e.target.value })}
          min="0"
        />

        <input
          type="text"
          className="post-title-input"
          placeholder="Website URL (optional, e.g., example.com)"
          value={newMentor.website}
          onChange={(e) => setNewMentor({ ...newMentor, website: e.target.value })}
        />

        <button type="submit" className="submit-button">
          Add Mentor
        </button>
      </form>
    </div>
  );
};

// Mentor Display Component (for both teachers and students)
export const MentorDisplay = ({ mentors, isTeacher, currentUser, userProfile, mentorRequests }) => {
  const [editingMentor, setEditingMentor] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    bio: '',
    subjects: '',
    hoursAvailable: '',
    website: ''
  });
  const [showRequestModal, setShowRequestModal] = useState(null); // mentorId when modal is open
  const [requestForm, setRequestForm] = useState({
    projectDescription: ''
  });

  const handleEdit = (mentor) => {
    setEditingMentor(mentor.id);
    setEditForm({
      name: mentor.name,
      bio: mentor.bio,
      subjects: mentor.subjects,
      hoursAvailable: mentor.hoursAvailable || '',
      website: mentor.website || ''
    });
  };

  const handleUpdate = async (mentorId) => {
    if (!editForm.name.trim() || !editForm.bio.trim() || !editForm.subjects.trim()) {
      alert('Please fill in at least the name, bio, and subjects fields.');
      return;
    }

    // Normalize website URL - add https:// if no protocol is specified, and www. if missing
    let normalizedWebsite = '';
    if (editForm.website.trim()) {
      normalizedWebsite = editForm.website.trim();
      // Add protocol if missing
      if (!normalizedWebsite.match(/^https?:\/\//i)) {
        normalizedWebsite = 'https://' + normalizedWebsite;
      }
      // Add www. if missing (after the protocol)
      if (!normalizedWebsite.match(/^https?:\/\/www\./i)) {
        normalizedWebsite = normalizedWebsite.replace(/^(https?:\/\/)/, '$1www.');
      }
    }

    try {
      await updateDoc(doc(db, 'mentors', mentorId), {
        name: editForm.name.trim(),
        bio: editForm.bio.trim(),
        subjects: editForm.subjects.trim(),
        hoursAvailable: editForm.hoursAvailable.trim() || '0',
        website: normalizedWebsite
      });
      setEditingMentor(null);
    } catch (error) {
      console.error('Error updating mentor:', error);
      alert('Failed to update mentor. Please try again.');
    }
  };

  const handleDelete = async (mentorId) => {
    if (!window.confirm('Are you sure you want to delete this mentor?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'mentors', mentorId));
    } catch (error) {
      console.error('Error deleting mentor:', error);
      alert('Failed to delete mentor. Please try again.');
    }
  };

  const cancelEdit = () => {
    setEditingMentor(null);
    setEditForm({
      name: '',
      bio: '',
      subjects: '',
      hoursAvailable: '',
      website: ''
    });
  };

  const handleRequestConnect = async (mentorId, mentorName) => {
    if (!requestForm.projectDescription.trim()) {
      alert('Please describe your project.');
      return;
    }

    try {
      // Create the mentor request - notification will be generated automatically
      await addDoc(collection(db, 'mentorRequests'), {
        mentorId,
        mentorName,
        studentId: currentUser.uid,
        studentName: userProfile?.firstName || userProfile?.name || 'Student',
        studentEmail: currentUser.email,
        projectDescription: requestForm.projectDescription.trim(),
        status: 'pending',
        createdAt: serverTimestamp()
      });

      setShowRequestModal(null);
      setRequestForm({ projectDescription: '' });
    //   alert('Connection request sent successfully!');
    } catch (error) {
      console.error('Error sending request:', error);
      alert('Failed to send request. Please try again.');
    }
  };

  const closeRequestModal = () => {
    setShowRequestModal(null);
    setRequestForm({ projectDescription: '' });
  };

  // Get requests for a specific mentor
  const getRequestsForMentor = (mentorId) => {
    if (!mentorRequests) return [];
    return mentorRequests.filter(req => req.mentorId === mentorId);
  };

  // Get student's own requests for a specific mentor
  const getStudentRequestsForMentor = (mentorId) => {
    if (!mentorRequests || !currentUser) return [];
    return mentorRequests.filter(req => 
      req.mentorId === mentorId && req.studentId === currentUser.uid
    );
  };

  // Handle marking request as complete (teacher only)
  const handleMarkRequestComplete = async (requestId) => {
    try {
      // Mark the request as complete - notification will automatically disappear
      await updateDoc(doc(db, 'mentorRequests', requestId), {
        status: 'complete',
        completedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error marking request as complete:', error);
      alert('Failed to mark request as complete. Please try again.');
    }
  };

  if (mentors.length === 0) {
    return (
      <div className="post post-margin-bottom">
        <h2>Available Mentors</h2>
        <p className="no-posts no-posts-padding">No mentors have been added yet.</p>
      </div>
    );
  }

  return (
    <div className="post post-margin-bottom">
      <h2>Available Mentors</h2>
      <p className="transcript-description">
        Connect with experienced mentors who can guide you on your projects. Your requests will be reviewed by a teacher and you will be notified when a connection is made.
      </p>
      
      <div className="mentor-list">
        {mentors.map((mentor) => (
          <div key={mentor.id} className="mentor-card">
            {editingMentor === mentor.id ? (
              // Edit mode
              <div className="mentor-edit-form">
                <input
                  type="text"
                  className="post-title-input"
                  placeholder="Mentor Name"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                />

                <textarea
                  placeholder="Mentor Bio"
                  value={editForm.bio}
                  onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                  rows="4"
                />

                <input
                  type="text"
                  className="post-title-input"
                  placeholder="Subjects"
                  value={editForm.subjects}
                  onChange={(e) => setEditForm({ ...editForm, subjects: e.target.value })}
                />

                <input
                  type="number"
                  className="post-title-input"
                  placeholder="Hours Available"
                  value={editForm.hoursAvailable}
                  onChange={(e) => setEditForm({ ...editForm, hoursAvailable: e.target.value })}
                  min="0"
                />

                <input
                  type="text"
                  className="post-title-input"
                  placeholder="Website URL (e.g., example.com)"
                  value={editForm.website}
                  onChange={(e) => setEditForm({ ...editForm, website: e.target.value })}
                />

                <div className="mentor-edit-actions">
                  <button 
                    onClick={() => handleUpdate(mentor.id)}
                    className="proposal-save-btn"
                  >
                    Save Changes
                  </button>
                  <button 
                    onClick={cancelEdit}
                    className="proposal-cancel-btn"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              // Display mode
              <>
                <div className="mentor-header">
                  <h3 className="post-title">{mentor.name}</h3>
                  {isTeacher && (
                    <div className="mentor-actions">
                      <button 
                        onClick={() => handleEdit(mentor)}
                        className="proposal-edit-btn"
                      >
                        Edit
                      </button>
                      <button 
                        onClick={() => handleDelete(mentor.id)}
                        className="proposal-reject-btn"
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
                
                <div className="mentor-bio">
                  <p>{mentor.bio}</p>
                </div>

                <div className="mentor-details">
                  <div className="mentor-detail-item">
                    <strong>Subjects:</strong> {mentor.subjects}
                  </div>
                  
                  {mentor.hoursAvailable && mentor.hoursAvailable !== '0' && (
                    <div className="mentor-detail-item">
                      <strong>Available Hours:</strong> {mentor.hoursAvailable} hours per year
                    </div>
                  )}
                  
                  {mentor.website && (
                    <div className="mentor-detail-item">
                      <strong>Website:</strong>{' '}
                      <a 
                        href={mentor.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="post-link"
                      >
                        {mentor.website}
                      </a>
                    </div>
                  )}
                </div>

                {/* Student: Show Request to Connect button and their requests */}
                {!isTeacher && currentUser && (
                  <>
                    <div className="mentor-request-section">
                      <button
                        onClick={() => setShowRequestModal(mentor.id)}
                        className="proposal-approve-btn mentor-request-button"
                      >
                        Request to Connect
                      </button>
                    </div>

                    {/* Show student's own requests for this mentor */}
                    {(() => {
                      const myRequests = getStudentRequestsForMentor(mentor.id);
                      if (myRequests.length > 0) {
                        return (
                          <div className="mentor-requests-list">
                            <h4 className="mentor-requests-header">
                              Your Connection Request{myRequests.length > 1 ? 's' : ''}
                            </h4>
                            {myRequests.map((request) => (
                              <div key={request.id} className="mentor-request-item">
                                <div className="mentor-request-header">
                                  <strong className="mentor-request-status">
                                    {request.status === 'complete' ? 'Completed' : 'Pending'}
                                  </strong>
                                  <span className="timestamp">
                                    {request.createdAt?.toDate?.().toLocaleDateString() || 'Recently'}
                                  </span>
                                </div>
                                <div className="mentor-request-description">
                                  <strong>Your Project:</strong> {request.projectDescription}
                                </div>
                                {request.status === 'complete' && request.completedAt && (
                                  <div className="mentor-request-complete-info">
                                    <i className="fa-solid fa-check mentor-request-check-icon"></i>
                                    Request completed {request.completedAt?.toDate?.().toLocaleDateString() || ''}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </>
                )}

                {/* Teacher: Show connection requests */}
                {isTeacher && (() => {
                  const requests = getRequestsForMentor(mentor.id);
                  if (requests.length > 0) {
                    return (
                      <div className="mentor-requests-list">
                        <h4 className="mentor-requests-header">
                          Connection Requests ({requests.length})
                        </h4>
                        {requests.map((request) => (
                          <div key={request.id} className="mentor-request-item">
                            <div className="mentor-request-header">
                              <strong className="mentor-request-status">
                                {request.studentName}
                                {request.status === 'complete' && (
                                  <span className="mentor-request-completed-badge">
                                    <i className="fa-solid fa-check"></i> Completed
                                  </span>
                                )}
                              </strong>
                              <span className="timestamp">
                                {request.createdAt?.toDate?.().toLocaleDateString() || 'Recently'}
                              </span>
                            </div>
                            <div className="mentor-request-description">
                              <strong>Project:</strong> {request.projectDescription}
                            </div>
                            {request.studentEmail && (
                              <div className="mentor-request-email">
                                <strong>Email:</strong>{' '}
                                <a href={`mailto:${request.studentEmail}`} className="post-link">
                                  {request.studentEmail}
                                </a>
                              </div>
                            )}
                            {request.status !== 'complete' && (
                              <div className="mentor-request-actions mentor-request-action-buttons">
                                <button
                                  onClick={() => handleMarkRequestComplete(request.id)}
                                  className="proposal-approve-btn mentor-request-complete-button"
                                >
                                  Mark as Complete
                                </button>
                              </div>
                            )}
                            {request.status === 'complete' && request.completedAt && (
                              <div className="mentor-request-complete-info">
                                <i className="fa-solid fa-check mentor-request-check-icon"></i>
                                Completed {request.completedAt?.toDate?.().toLocaleDateString() || ''}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  }
                  return null;
                })()}
              </>
            )}
          </div>
        ))}
      </div>

      {/* Request to Connect Modal */}
      {showRequestModal && (
        <div className="modal-overlay" onClick={closeRequestModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Request to Connect</h2>
            <p className="mentor-request-modal-description">
              Tell {mentors.find(m => m.id === showRequestModal)?.name} about your project
            </p>
            
            <label>Project Description *</label>
            <textarea
              placeholder="Describe the project you need help with..."
              value={requestForm.projectDescription}
              onChange={(e) => setRequestForm({ projectDescription: e.target.value })}
              rows="6"
              className="mentor-request-textarea"
            />

            <button
              onClick={() => handleRequestConnect(
                showRequestModal,
                mentors.find(m => m.id === showRequestModal)?.name
              )}
              className="proposal-approve-btn"
            >
              Send Request
            </button>

            <button
              onClick={closeRequestModal}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

